import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Facebook, Instagram, ArrowRight } from './icons';
import { SlotMachineButton } from './SlotMachineButton';
import footerLogo from '../assets/images/Header/logo.svg';
import { useFooterSection } from '../hooks/useFooterSection';
import { useGA4 } from '../hooks/useGA4';
import { getCurrentLocale } from '../services/api';
import { getBestImageUrl } from '../utils/imageUrl';
import { useBooking } from '../contexts/BookingContext';

function Footer(): React.JSX.Element {
  const { data, loading, error } = useFooterSection();
  const footerData = (data as any)?.attributes ? (data as any).attributes : (data as any);
  const locale = getCurrentLocale();
  const phoneLabel = locale === 'ar' ? 'الهاتف' : 'Telefoon';
  const { openBookingPopup } = useBooking();
  
  // GA4 tracking
  const { trackMenuClick, trackEvent } = useGA4();

  // Debug logging for footer section data
  if (footerData && !loading && !error) {
    console.log('🦶 Footer - ✅ Strapi data loaded:', !!data);
    console.log('🦶 Footer - Data keys:', Object.keys(data || {}));
    console.log('🦶 Footer - CTA Title:', footerData.ctaTitle);
    console.log('🦶 Footer - Has logo:', !!footerData.logo);
    console.log('🦶 Footer - Navigation links count:', footerData.navigationLinks?.length || 0);
    console.log('🦶 Footer - Legal links count:', footerData.legalLinks?.length || 0);
  }
  
  // Debug logging for loading states
  if (loading) {
    console.log('🦶 Footer - Loading Strapi data...');
  }
  
  if (error) {
    console.log('🦶 Footer - ❌ Error loading Strapi data:', error);
    console.log('🦶 Footer - Falling back to default data');
  }
  const handleLinkClick = (linkLabel?: string, linkUrl?: string): void => {
    // Track footer navigation click
    if (linkLabel && linkUrl) {
      trackMenuClick(linkLabel, 'footer');
    }
    
    // Scroll to top of the page when navigating
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBookingClick = (e: React.MouseEvent): void => {
    e.preventDefault();
    // Track booking popup open
    trackEvent('booking_popup_open', {
      location: 'footer',
      device_type: 'desktop'
    });
    openBookingPopup();
  };

  // Default fallback data
  const defaultData = {
    ctaTitle: "Maak direct een afspraak",
    ctaDescription: "Neem contact met ons op voor een gratis consultatie en ontdek hoe wij jou kunnen helpen.",
    ctaButtonText: "Afspraak maken",
    navigationTitle: "Hairmedics",
    navigationLinks: [
      { label: "Behandelingen", url: "/behandelingen" },
      { label: "Tarieven", url: "/tarieven" },
      { label: "Over ons", url: "/over-ons" },
      { label: "Contact", url: "/contact" }
    ],
    contactTitle: "Contact",
    contactAddress: "Kennemerstraatweg 464\nKamer 1.28\n1851 NG Heiloo",
    contactPhone: "072 5338718",
    contactHours: "Maandag tot en met vrijdag van 09:00 tot 17:30",
    googleMapsUrl: "https://maps.google.com/?q=Kennemerstraatweg+464,+1851+NG+Heiloo",
    copyrightText: "Hairmedics. Alle rechten voorbehouden.",
    madeByText: "Made with ⚡️ by",
    madeByLinkText: "Alt F Awesome",
    madeByLinkUrl: "https://altfawesome.com",
    legalLinks: [
      { label: "Algemene voorwaarden", url: "/algemene-voorwaarden" },
      { label: "Privacy Policy", url: "/privacy-policy" }
    ]
  };

  // Use Strapi data when available; only fall back to defaults if the entire
  // footer payload is missing (e.g., API offline). Do not mix per-field
  // defaults across locales, to avoid showing Dutch content in Arabic.
  const useDefaults = !footerData;
  const logoUrl = footerData?.logo ? (getBestImageUrl(footerData.logo) || footerLogo) : footerLogo;
  const navigationLinks = useDefaults
    ? defaultData.navigationLinks
    : Array.isArray(footerData?.navigationLinks)
      ? (footerData?.navigationLinks as typeof defaultData.navigationLinks)
      : [];
  const legalLinks = useDefaults
    ? defaultData.legalLinks
    : Array.isArray(footerData?.legalLinks)
      ? (footerData?.legalLinks as typeof defaultData.legalLinks)
      : [];

  const ctaTitle = useDefaults ? defaultData.ctaTitle : (footerData?.ctaTitle ?? '');
  const ctaDescription = useDefaults ? defaultData.ctaDescription : (footerData?.ctaDescription ?? '');
  const ctaButtonText = useDefaults ? defaultData.ctaButtonText : (footerData?.ctaButtonText ?? '');
  // Force locale-specific CTA text to avoid cross-locale bleed from Strapi
  const defaultCtaText = locale === 'ar' ? 'احجز موعداً' : 'Afspraak maken';
  const finalCtaButtonText = ctaButtonText || defaultCtaText;
  const navigationTitle = useDefaults ? defaultData.navigationTitle : (footerData?.navigationTitle ?? '');
  const contactTitle = useDefaults ? defaultData.contactTitle : (footerData?.contactTitle ?? '');
  const contactAddress = useDefaults ? defaultData.contactAddress : (footerData?.contactAddress ?? '');
  const contactPhone = useDefaults ? defaultData.contactPhone : (footerData?.contactPhone ?? '');
  const contactHours = useDefaults ? defaultData.contactHours : (footerData?.contactHours ?? '');
  const googleMapsUrl = useDefaults ? defaultData.googleMapsUrl : (footerData?.googleMapsUrl ?? '#');
  const copyrightText = useDefaults ? defaultData.copyrightText : (footerData?.copyrightText ?? '');
  const madeByText = useDefaults ? defaultData.madeByText : (footerData?.madeByText ?? '');
  const madeByLinkText = useDefaults ? defaultData.madeByLinkText : (footerData?.madeByLinkText ?? '');
  const madeByLinkUrl = useDefaults ? defaultData.madeByLinkUrl : (footerData?.madeByLinkUrl ?? '#');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { delay: 0.4, staggerChildren: 0.2, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <motion.footer
      className="footer"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      {/* CTA Section */}
      <motion.div className="footer-cta" variants={itemVariants}>
        <div className="cta-content">
          <h2 className="section-title text-center">{ctaTitle}</h2>
          <p className="cta-description text-center">
            {ctaDescription}
          </p>
          <div className="cta-actions">
            <SlotMachineButton
              text={finalCtaButtonText}
              icon={<ArrowRight />}
              onClick={handleBookingClick}
              className="btn btn-primary"
              variant="primary"
            />
          </div>
        </div>
      </motion.div>

      {/* Footer Content */}
      <motion.div className="footer-content" variants={itemVariants}>
        <div className="footer-section">
          <div className="footer-logo">
            <Link to="/" onClick={() => handleLinkClick('Footer Logo', '/')}>
              <img 
                src={logoUrl || undefined} 
                alt={(footerData?.logo?.alternativeText as string) || "Hairmedics - Excellence"} 
                className="logo-img" 
              />
            </Link>
          </div>
        </div>
        
        <div className="footer-right-column">
          <div className="footer-section">
            <h3 className="footer-title">{navigationTitle}</h3>
            <ul className="footer-links">
              {navigationLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.url} onClick={() => handleLinkClick(link.label, link.url)}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="footer-section">
            <h3 className="footer-title">{contactTitle}</h3>
            <div className="footer-address">
              <div>
                <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
                  {(() => {
                    const lines = (contactAddress || '').split('\n');
                    return lines.map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < lines.length - 1 && <br />}
                      </React.Fragment>
                    ));
                  })()}
                </a>
              </div>
              <div>{phoneLabel}: <a href={`tel:+31${contactPhone}`}>{contactPhone}</a></div>
              <div>{contactHours}</div>
            </div>
          </div>
        </div>
      </motion.div>
      
      <motion.div className="footer-bottom" variants={itemVariants}>
        <div className="footer-bottom-content">
          <div className="footer-copyright">
            <p className="paragraph-s">
              &copy; {new Date().getFullYear()} {copyrightText} | {madeByText}{' '}
              <a 
                href={madeByLinkUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-link" 
                style={{ textDecoration: 'underline' }}
              >
                {madeByLinkText}
              </a>
            </p>
          </div>
          <div className="footer-social">
            {legalLinks.map((link, index) => (
              <Link 
                key={index} 
                to={link.url} 
                className="paragraph-s" 
                onClick={() => handleLinkClick(link.label, link.url)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.footer>
  );
}

export default Footer; 